// Write the current tab into the path, which leads to a more natural navigation and the possibility to link to tabs on pages

import { isNil } from "lodash";
import {
  type ComponentPublicInstance,
  nextTick,
  onBeforeMount,
  ref,
  watch,
} from "vue";
import { useRouter } from "vue-router";

/**
 * Usage: returns the currentTab reference which has to be used in el-tabs, has to be initialized with the starting tab
 * @param initialTab the inital tab
 * @returns currentTab, the reference to be used by el-tabs
 */
export function usePersistantTabs(initialTab: string) {
  const router = useRouter();
  const currentTab = ref(initialTab);
  const tabsRef = ref<ComponentPublicInstance | null>(null);

  onBeforeMount(() => {
    if (router.currentRoute.value.query?.tab) {
      currentTab.value = router.currentRoute.value.query.tab as string;
    }
  });

  watch(currentTab, () => {
    // Update URL query parameter without triggering navigation guards
    const route = router.currentRoute.value;
    const newQuery = {
      ...route.query,
      tab: currentTab.value,
    };

    // Use router.replace to update URL without adding to history
    // and without triggering beforeEach guards on the same route
    void router.replace({
      name: route.name,
      params: route.params,
      query: newQuery,
      hash: route.hash,
    });
  });

  watch(
    () => router.currentRoute.value.query?.tab,
    () => {
      if (
        !isNil(router.currentRoute.value.query?.tab) &&
        router.currentRoute.value.query?.tab !== currentTab.value
      ) {
        currentTab.value = router.currentRoute.value.query.tab as string;
      }
    },
  );
  return { currentTab, tabsRef };
}
